import { CommonModule } from '@angular/common';
import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import type { ServiceOffering } from '@freelancer/datastore/collections';
import { PipesModule } from '@freelancer/pipes';
import { TrackingModule } from '@freelancer/tracking';
import { ModalService, UiModule } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { ModalColor } from '@freelancer/ui/modal';
import {
  PictureLayout,
  PictureLoadMethod,
  PictureObjectFit,
} from '@freelancer/ui/picture';
import {
  FontColor,
  FontWeight,
  TextAlign,
  TextSize,
} from '@freelancer/ui/text';
import { isDefined } from '@freelancer/utils';
import { Observable, filter, map, shareReplay } from 'rxjs';
import { Service } from '../ai-services/ai-services.types';

@Component({
  standalone: true,
  imports: [CommonModule, PipesModule, TrackingModule, UiModule],
  selector: 'app-ai-service-item',
  template: `
    <div class="Content">
      <div>
        <div
          class="Header"
          [flMarginBottom]="'xsmall'"
          [flMarginBottomTablet]="'small'"
        >
          <fl-icon
            [color]="IconColor.PRIMARY"
            [name]="service.headerIcon"
            [fillIconFont]="false"
            [useIconFont]="true"
            [size]="IconSize.SMALL"
            [flMarginRight]="'xsmall'"
          ></fl-icon>
          <p class="text-light text-large tablet:text-large font-bold">
            {{ service.header }}
          </p>
        </div>

        <p
          class="text-light text-xsmall tablet:text-small"
          [flMarginBottom]="'small'"
          [flMarginBottomTablet]="'large'"
        >
          {{ service.description }}
        </p>
        <div
          class="ImageContainer"
          flTrackingLabel="VideoDemoPlayed"
          (click)="handleClickVideoPreview()"
        >
          <fl-picture
            class="Image"
            [height]="800"
            [width]="1000"
            [layout]="PictureLayout.RESPONSIVE"
            [loadMethod]="PictureLoadMethod.LAZY"
            [objectFit]="PictureObjectFit.COVER"
            [src]="service.src"
          ></fl-picture>
          <div
            class="IconTextContainer"
            [class.IconTextContainer-homePage]="isHomePage"
          >
            <fl-picture
              class="ImageIcon"
              [height]="57"
              [width]="57"
              [layout]="PictureLayout.RESPONSIVE"
              [loadMethod]="PictureLoadMethod.LAZY"
              [src]="'back-to-work/ai-services/v2/play_circle.svg'"
              [flMarginBottom]="'xxsmall'"
              [flMarginBottomTablet]="'small'"
              [flMarginBottomDesktop]="'xxsmall'"
            ></fl-picture>
            <p
              class="text-light text-xxsmall tablet:text-large desktop:text-xxxsmall desktop_large:text-xxsmall font-black text-center"
              i18n="Watch a demo"
            >
              Watch a demo
            </p>
          </div>
        </div>
      </div>

      <div>
        <fl-text
          [color]="FontColor.LIGHT"
          [size]="TextSize.XSMALL"
          [sizeTablet]="TextSize.SMALL"
          [textAlign]="TextAlign.CENTER"
          [weight]="FontWeight.BOLD"
          [flMarginBottom]="
            isDefined(serviceOfferingDiscountedPrice$ | flAsync)
              ? 'none'
              : 'xxsmall'
          "
          [flMarginBottomTablet]="
            isDefined(serviceOfferingDiscountedPrice$ | flAsync)
              ? 'none'
              : 'xsmall'
          "
        >
          {{ typicallyText }}
          {{
            service.typicalTotalPrice
              | flCurrency: 'USD' : { disableCode: true, withPrecision: false }
          }}
        </fl-text>
        <p
          class="text-light text-xsmall tablet:text-small text-center font-bold"
          *ngIf="isDefined(serviceOfferingDiscountedPrice$ | flAsync)"
          i18n="Consultation price button text"
          [flMarginBottom]="'small'"
        >
          Consultation for only
        </p>
        <fl-button
          class="Button"
          [busy]="!(isServiceOfferingsCollectionReady$ | flAsync)"
          [color]="ButtonColor.PRIMARY"
          [display]="'flex'"
          [flTrackingLabel]="service.type + 'Button'"
          [label]="service.type + 'Button'"
          [link]="service.link"
          [size]="ButtonSize.MID"
          [sizeTablet]="ButtonSize.LARGE"
        >
          <div
            *ngIf="serviceOffering$ | flAsync as serviceOffering"
            class="Button-text"
          >
            <p
              class="text-color-inherit text-xsmall text-right font-weight-inherit"
              *ngIf="!isDefined(serviceOfferingDiscountedPrice$ | flAsync)"
              i18n="Consultation price button text"
              [flMarginRight]="'xxsmall'"
              [flMarginRightTablet]="'xxsmall'"
            >
              Consultation for only
            </p>
            <fl-text
              [color]="FontColor.INHERIT"
              [size]="TextSize.XXXLARGE"
              [weight]="
                isDefined(serviceOfferingDiscountedPrice$ | flAsync)
                  ? FontWeight.NORMAL
                  : FontWeight.BOLD
              "
              [textDecoration]="
                isDefined(serviceOfferingDiscountedPrice$ | flAsync)
                  ? 'line-through'
                  : undefined
              "
            >
              {{
                serviceOffering.price
                  | flCurrency
                    : 'USD'
                    : { disableCode: true, withPrecision: false }
              }}
            </fl-text>
            <p
              class="text-color-inherit text-xxxlarge font-bold"
              *ngIf="isDefined(serviceOfferingDiscountedPrice$ | flAsync)"
            >
              &nbsp;{{
                serviceOfferingDiscountedPrice$
                  | flAsync
                  | flCurrency
                    : 'USD'
                    : { disableCode: true, withPrecision: false }
              }}
            </p>
          </div>
        </fl-button>
      </div>
    </div>
  `,
  styleUrls: ['./ai-service-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiServiceItemComponent implements OnInit {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  FontColor = FontColor;
  FontWeight = FontWeight;
  IconColor = IconColor;
  IconSize = IconSize;
  PictureLayout = PictureLayout;
  PictureLoadMethod = PictureLoadMethod;
  PictureObjectFit = PictureObjectFit;
  TextAlign = TextAlign;
  TextSize = TextSize;

  isDefined = isDefined;

  @Input() service: Service;
  @Input() isHomePage = false;
  @Input() filteredAiServiceOfferings$: Observable<readonly ServiceOffering[]>;
  @Input() isServiceOfferingsCollectionReady$: Observable<boolean>;

  partnerSrc?: string;
  typicallyText = $localize`Typically from`;

  serviceOffering$: Observable<ServiceOffering>;
  serviceOfferingDiscountedPrice$: Observable<number | undefined>;

  constructor(private modalService: ModalService) {}

  ngOnInit(): void {
    this.serviceOffering$ = this.filteredAiServiceOfferings$.pipe(
      map(filteredAiServiceOfferings =>
        filteredAiServiceOfferings.find(
          serviceOffering =>
            serviceOffering.seoUrl &&
            serviceOffering.seoUrl ===
              this.service.link.replace('/service/', ''),
        ),
      ),
      filter(isDefined),
    );

    this.serviceOfferingDiscountedPrice$ = this.serviceOffering$.pipe(
      map(aiActionPlanService => {
        const now = Date.now();
        const hasActiveDiscount = aiActionPlanService.discountDetails
          ? now >= aiActionPlanService.discountDetails.validFrom &&
            now <= aiActionPlanService.discountDetails.validUntil
          : false;

        return hasActiveDiscount
          ? aiActionPlanService.discountDetails?.discountedPrice
          : undefined;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  handleClickVideoPreview(): void {
    this.modalService.open('VideoModal', {
      inputs: {
        src: this.service.videoSrc,
      },
      edgeToEdge: true,
      closeable: true,
      color: ModalColor.DARK,
    });
  }
}
